/**
 * Minimal IndexedDB wrapper for SIP-Pilkades offline-first Coklit.
 *
 * Object stores:
 *  - voters: { id (key), ...voterFields, _cachedAt }
 *  - verify_queue: { qid (key, auto), voter_id, verified, at, attempts }
 *  - meta: { key (key), value }    e.g. { key: "voters_last_sync", value: 17... }
 *
 * Why native IDB (no dep): keep bundle small for entry-level phones.
 */
const DB_NAME = "sip-pilkades-offline";
const DB_VERSION = 1;

let _dbPromise = null;

export function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB tidak tersedia di browser ini"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains("voters")) {
        const s = db.createObjectStore("voters", { keyPath: "id" });
        s.createIndex("nik", "nik", { unique: false });
        s.createIndex("dusun", "dusun", { unique: false });
      }
      if (!db.objectStoreNames.contains("verify_queue")) {
        const s = db.createObjectStore("verify_queue", { keyPath: "qid", autoIncrement: true });
        s.createIndex("voter_id", "voter_id", { unique: false });
      }
      if (!db.objectStoreNames.contains("meta")) {
        db.createObjectStore("meta", { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("IndexedDB open failed"));
  });
  return _dbPromise;
}

function tx(db, names, mode = "readonly") {
  return db.transaction(names, mode);
}

function reqAsPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ---------- Voter cache ----------

const MAX_CACHED_VOTERS = 2500; // entry-level guard

export async function cacheVoters(voters) {
  if (!Array.isArray(voters) || voters.length === 0) return;
  const db = await openDb();
  const t = tx(db, ["voters"], "readwrite");
  const store = t.objectStore("voters");
  const stamp = Date.now();
  // LRU-ish: if cache too big, clear oldest before writing new batch
  const countReq = store.count();
  countReq.onsuccess = () => {
    if (countReq.result + voters.length > MAX_CACHED_VOTERS) {
      store.clear();
    }
    for (const v of voters) {
      if (v && v.id) {
        store.put({ ...v, _cachedAt: stamp });
      }
    }
  };
  return new Promise((res, rej) => {
    t.oncomplete = res;
    t.onerror = () => rej(t.error);
  });
}

export async function readCachedVoters({ q = "", dusun = null, limit = 200 } = {}) {
  const db = await openDb();
  const store = tx(db, ["voters"], "readonly").objectStore("voters");
  const all = await reqAsPromise(store.getAll());
  const ql = q.trim().toLowerCase();
  const filtered = all
    .filter((v) => !dusun || v.dusun === dusun)
    .filter((v) => {
      if (!ql) return true;
      return (
        (v.nama || "").toLowerCase().includes(ql) ||
        (v.nik || "").includes(q.trim())
      );
    })
    .sort((a, b) => (a.nama || "").localeCompare(b.nama || ""))
    .slice(0, limit);
  return filtered;
}

export async function patchCachedVoter(id, patch) {
  const db = await openDb();
  const t = tx(db, ["voters"], "readwrite");
  const store = t.objectStore("voters");
  const existing = await reqAsPromise(store.get(id));
  if (existing) {
    store.put({ ...existing, ...patch });
  }
  return new Promise((res) => { t.oncomplete = res; });
}

// ---------- Verify queue ----------

export async function enqueueVerify(voter_id, verified) {
  const db = await openDb();
  const t = tx(db, ["verify_queue"], "readwrite");
  const store = t.objectStore("verify_queue");
  // Drop any prior queued action for same voter (we only need the latest intent)
  const idx = store.index("voter_id");
  const cursorReq = idx.openCursor(IDBKeyRange.only(voter_id));
  cursorReq.onsuccess = (e) => {
    const cur = e.target.result;
    if (cur) {
      cur.delete();
      cur.continue();
    }
  };
  store.add({ voter_id, verified: !!verified, at: Date.now(), attempts: 0 });
  return new Promise((res) => { t.oncomplete = res; });
}

export async function listVerifyQueue() {
  const db = await openDb();
  const store = tx(db, ["verify_queue"], "readonly").objectStore("verify_queue");
  return reqAsPromise(store.getAll());
}

export async function countVerifyQueue() {
  const db = await openDb();
  const store = tx(db, ["verify_queue"], "readonly").objectStore("verify_queue");
  return reqAsPromise(store.count());
}

export async function deleteVerifyQueueItem(qid) {
  const db = await openDb();
  const t = tx(db, ["verify_queue"], "readwrite");
  t.objectStore("verify_queue").delete(qid);
  return new Promise((res) => { t.oncomplete = res; });
}

export async function bumpVerifyAttempts(qid) {
  const db = await openDb();
  const t = tx(db, ["verify_queue"], "readwrite");
  const store = t.objectStore("verify_queue");
  const it = await reqAsPromise(store.get(qid));
  if (it) {
    store.put({ ...it, attempts: (it.attempts || 0) + 1, lastError: Date.now() });
  }
  return new Promise((res) => { t.oncomplete = res; });
}

// ---------- Meta ----------

export async function getMeta(key) {
  const db = await openDb();
  const store = tx(db, ["meta"], "readonly").objectStore("meta");
  const row = await reqAsPromise(store.get(key));
  return row?.value;
}

export async function setMeta(key, value) {
  const db = await openDb();
  const t = tx(db, ["meta"], "readwrite");
  t.objectStore("meta").put({ key, value });
  return new Promise((res) => { t.oncomplete = res; });
}

// ---------- Quota guard ----------

export async function getStorageEstimate() {
  if (!navigator?.storage?.estimate) return null;
  try {
    const { usage = 0, quota = 0 } = await navigator.storage.estimate();
    return { usageMB: usage / (1024 * 1024), quotaMB: quota / (1024 * 1024) };
  } catch {
    return null;
  }
}

// Clears voter cache (keeps queue intact so user doesn't lose pending work).
export async function clearVoterCache() {
  const db = await openDb();
  const t = tx(db, ["voters"], "readwrite");
  t.objectStore("voters").clear();
  return new Promise((res) => { t.oncomplete = res; });
}
